import React, { PropsWithChildren, ReactNode } from 'react';
import { style, classes } from 'typestyle';
import { ADALONG_COLORS, ThemeStyle } from '@src/styles';
import { SearchElementRef } from '@src/types/Search';
import { color } from 'csx';
import { SearchSvgIcon } from './SvgIcons/SearchIcon';
import { CloseButton } from './CloseButton';

type SearchBarAppearance = 'light' | 'dark';

interface Props {
  handlers: {
    onBarRef?(elem: HTMLElement | null): void
    onInputRef?(elem: HTMLElement | null): void
    onText(val: string): void
    onInputFocus?(): void
    onCloseBar(): void
  }
  elementRefs?: SearchElementRef
  inputValue: string
  description?: string
  className?: string | string[]
  rightSlot?: ReactNode
  appearance?: SearchBarAppearance
  layout?: 'singleLine' | 'stacked'
}

/**
 * Big white search bar with
 * Give selected content as children
 */
export function SearchAndSelectBar({
  children,
  handlers,
  elementRefs,
  inputValue,
  description = 'Search',
  className,
  rightSlot,
  appearance = 'light',
  layout = 'singleLine',
}: PropsWithChildren<Props>) {
  const closePalette = closeButtonPalette[appearance];
  const hasContent = inputValue.trim().length > 0 || React.Children.count(children) > 0;
  const shouldDisplaySearchIcon = !(layout === 'stacked' && hasContent);
  return (
    <div className={classes(barBaseClass, barVariantClass[appearance], className)} ref={elementRefs?.bar}>
      {shouldDisplaySearchIcon ? (
        <div className={classes(searchIconBaseClass, searchIconVariantClass[appearance])}>
          <SearchSvgIcon size={14} style={{ marginBottom: 2 }} />
        </div>
      ) : null}
      <div className={classes(coreAreaClass, coreAreaVariantClass[layout])}>
        <>
          {children}
        </>
        <span className={classes(inputBaseClass, inputVariantClass[appearance], inputLayoutClass[layout])}>
          <input
            placeholder={description}
            ref={elementRefs?.text}
            value={inputValue}
            onChange={(e) => handlers.onText(e.target.value)}
            onFocus={() => handlers.onInputFocus?.()}
          />
        </span>
      </div>
      <div className={deleteContainerBaseClass}>
        <CloseButton
          size={28}
          color={closePalette.color}
          hoverColor={closePalette.hover}
          onClick={() => handlers.onCloseBar()}
          classNames={[deleteButtonClass]}
        />
      </div>
      {rightSlot ? (
        <div className={classes(rightSlotBaseClass, rightSlotVariantClass[appearance])}>{rightSlot}</div>
      ) : null}
    </div>
  );
}

const barBaseClass = style({
  display: 'flex',
  alignItems: 'center',
  lineHeight: 1.8,
  fontSize: ThemeStyle.textSizes.medium,
  fontWeight: 200,
  width: '100%',
  overflow: 'hidden',
  transition: 'background-color 0.2s ease, color 0.2s ease',
});

const barVariantClass: Record<SearchBarAppearance, string> = {
  light: style({
    backgroundColor: ADALONG_COLORS.SMOOTH_WHITE,
    color: ADALONG_COLORS.ABSOLUTE_BLACK,
    borderRadius: 36,
    padding: '4px 10px',
    border: '1px solid rgba(0,0,0,0.08)',
  }),
  dark: style({
    backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
    color: ADALONG_COLORS.SMOOTH_WHITE,
    borderRadius: 36,
    padding: '4px 8px',
    border: '1px solid rgba(255,255,255,0.1)',
  }),
};

const searchIconBaseClass = style({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
});

const searchIconVariantClass: Record<SearchBarAppearance, string> = {
  light: style({
    padding: '0 9px 0 15px',
    $nest: {
      svg: {
        verticalAlign: 'bottom',
        fill: ADALONG_COLORS.ABSOLUTE_BLACK,
      },
    },
  }),
  dark: style({
    padding: '0 10px 0 12px',
    $nest: {
      svg: {
        verticalAlign: 'bottom',
        fill: ADALONG_COLORS.SMOOTH_WHITE,
      },
    },
  }),
};

const coreAreaClass = style({
  flex: 1,
  display: 'flex',
  overflow: 'hidden',
  alignItems: 'center',
  minWidth: 0,
});

const coreAreaVariantClass: Record<'singleLine' | 'stacked', string> = {
  singleLine: style({
    flexWrap: 'nowrap',
  }),
  stacked: style({
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    padding: '6px 0',
  }),
};

const inputBaseClass = style({
  display: 'inline-block',
  flex: 1,
  $nest: {
    input: {
      verticalAlign: 'text-top',
      width: '100%',
      display: 'block',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 0,
      appearance: 'none',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      $nest: {
        '&:focus': {
          outline: 'none',
        },
      },
    },
  },
});

const inputLayoutClass: Record<'singleLine' | 'stacked', string> = {
  singleLine: style({}),
  stacked: style({
    flex: '1 0 100%',
    width: '100%',
    marginTop: 6,
  }),
};

const inputVariantClass: Record<SearchBarAppearance, string> = {
  light: style({
    $nest: {
      input: {
        color: ADALONG_COLORS.ABSOLUTE_BLACK,
      },
      'input::placeholder': {
        color: 'rgba(0,0,0,0.45)',
      },
    },
  }),
  dark: style({
    $nest: {
      input: {
        color: ADALONG_COLORS.SMOOTH_WHITE,
      },
      'input::placeholder': {
        color: 'rgba(255,255,255,0.45)',
      },
    },
  }),
};

const deleteContainerBaseClass = style({
  flexShrink: 0,
  padding: '0 6px',
  display: 'flex',
  alignItems: 'center',
  marginRight: 4,
});

const deleteButtonClass = style({
  verticalAlign: 'middle',
  padding: '4px 6px',
});

const rightSlotBaseClass = style({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 10px 0 8px',
});

const rightSlotVariantClass: Record<SearchBarAppearance, string> = {
  light: style({
    color: ADALONG_COLORS.ABSOLUTE_BLACK,
    borderLeft: '1px solid rgba(0,0,0,0.12)',
  }),
  dark: style({
    color: ADALONG_COLORS.SMOOTH_WHITE,
    borderLeft: '1px solid rgba(255,255,255,0.15)',
  }),
};

const closeButtonPalette: Record<SearchBarAppearance, { color: string; hover: string }> = {
  light: {
    color: ADALONG_COLORS.LIGHT_GRAY,
    hover: color(ADALONG_COLORS.LIGHT_GRAY).darken(0.2).toHexString(),
  },
  dark: {
    color: ADALONG_COLORS.SMOOTH_WHITE,
    hover: color(ADALONG_COLORS.SMOOTH_WHITE).fade(0.3).toHexString(),
  },
};
